import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, Pencil, Trash2, Plus } from "lucide-react";

const TABS = ["pending", "approved", "rejected"];
const input = "w-full border-2 border-[#D3CFC0] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#1E1E1E]";
const btn = "inline-flex items-center gap-1 px-3 py-1.5 text-xs font-head uppercase tracking-wide border-2 transition-colors";
const EMPTY = { text: "", first_name: "", sport: "", email: "" };

function AddForm({ api, onAdded }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (f.text.trim().length < 3) { toast.error("Paste the reply text first."); return; }
    setBusy(true);
    try { await api.post("/admin/testimonials", f); setF(EMPTY); toast.success("Added to Pending."); onAdded(); }
    catch { toast.error("Couldn't add it."); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={add} className="border-2 border-[#1E1E1E] bg-white p-5 space-y-3" data-testid="testimonial-add-form">
      <h3 className="font-head uppercase text-lg text-[#1E1E1E]">Paste a reply</h3>
      <textarea rows={5} value={f.text} onChange={set("text")} placeholder="Paste what the coach wrote..." className={input} data-testid="testimonial-add-text" />
      <div className="grid sm:grid-cols-3 gap-3">
        <input value={f.first_name} onChange={set("first_name")} placeholder="First name" className={input} data-testid="testimonial-add-name" />
        <input value={f.sport} onChange={set("sport")} placeholder="Sport" className={input} data-testid="testimonial-add-sport" />
        <input value={f.email} onChange={set("email")} placeholder="Their email (private, optional)" className={input} data-testid="testimonial-add-email" />
      </div>
      <button type="submit" disabled={busy} data-testid="testimonial-add-submit"
        className="inline-flex items-center gap-2 bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-5 py-2.5 hover:bg-[#D6922C] disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add to list
      </button>
    </form>
  );
}

function Item({ t, api, onChange }) {
  const [edit, setEdit] = useState(null);
  const patch = async (body, msg) => {
    try { await api.patch(`/admin/testimonials/${t.id}`, body); if (msg) toast.success(msg); setEdit(null); onChange(); }
    catch { toast.error("Update failed."); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this testimonial for good?")) return;
    try { await api.delete(`/admin/testimonials/${t.id}`); onChange(); } catch { toast.error("Delete failed."); }
  };
  return (
    <div className="border-2 border-[#D3CFC0] bg-white p-4" data-testid={`testimonial-${t.id}`}>
      {edit ? (
        <div className="space-y-2">
          <textarea rows={4} value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} className={input} data-testid="testimonial-edit-text" />
          <div className="grid grid-cols-2 gap-2">
            <input value={edit.first_name} onChange={(e) => setEdit({ ...edit, first_name: e.target.value })} placeholder="First name" className={input} data-testid="testimonial-edit-name" />
            <input value={edit.sport} onChange={(e) => setEdit({ ...edit, sport: e.target.value })} placeholder="Sport" className={input} data-testid="testimonial-edit-sport" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => patch(edit, "Saved.")} className={`${btn} border-[#1E1E1E] bg-[#1E1E1E] text-white`} data-testid="testimonial-edit-save">Save</button>
            <button onClick={() => setEdit(null)} className={`${btn} border-[#D3CFC0]`}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-[#1E1E1E] whitespace-pre-line" data-testid="testimonial-text">&ldquo;{t.text}&rdquo;</p>
          <p className="mt-2 text-sm text-[#6E6E6E]" data-testid="testimonial-byline">
            {[t.first_name || "No name", t.sport].filter(Boolean).join(" · ")}{t.email && <span className="ml-2 text-xs">({t.email})</span>}
          </p>
        </>
      )}
      {!edit && (
        <div className="mt-3 flex flex-wrap gap-2">
          {t.status !== "approved" && <button onClick={() => patch({ status: "approved" }, "Approved.")} className={`${btn} border-[#2E7D57] text-[#2E7D57] hover:bg-[#2E7D57] hover:text-white`} data-testid="testimonial-approve"><Check size={12} /> Approve</button>}
          {t.status !== "rejected" && <button onClick={() => patch({ status: "rejected" }, "Rejected.")} className={`${btn} border-[#B3261E] text-[#B3261E] hover:bg-[#B3261E] hover:text-white`} data-testid="testimonial-reject"><X size={12} /> Reject</button>}
          {t.status !== "pending" && <button onClick={() => patch({ status: "pending" })} className={`${btn} border-[#D3CFC0] text-[#6E6E6E]`} data-testid="testimonial-to-pending">Back to pending</button>}
          <button onClick={() => setEdit({ text: t.text, first_name: t.first_name, sport: t.sport })} className={`${btn} border-[#D3CFC0] text-[#1E1E1E]`} data-testid="testimonial-edit"><Pencil size={12} /> Edit</button>
          <button onClick={remove} className={`${btn} border-[#D3CFC0] text-[#6E6E6E]`} data-testid="testimonial-delete"><Trash2 size={12} /></button>
        </div>
      )}
    </div>
  );
}

export default function TestimonialsAdmin({ api }) {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const load = () => api.get(`/admin/testimonials?status=${status}`).then(({ data }) => setData(data)).catch(() => toast.error("Couldn't load testimonials."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  return (
    <div className="mt-8 grid lg:grid-cols-[1fr_1.3fr] gap-8 items-start" data-testid="testimonials-admin">
      <div>
        <AddForm api={api} onAdded={() => (status === "pending" ? load() : setStatus("pending"))} />
        <p className="mt-3 text-xs text-[#6E6E6E]">Replies to the Coach Ready certificate email land in your inbox. Paste the good ones here. Emails stay private; only you see this list.</p>
      </div>
      <div>
        <div className="flex gap-2">
          {TABS.map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`testimonials-tab-${s}`}
              className={`px-4 py-2 font-head uppercase tracking-wide text-xs border-2 ${status === s ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E]"}`}>
              {s} {data?.counts ? `(${data.counts[s]})` : ""}
            </button>
          ))}
        </div>
        <div className="mt-4 space-y-3" data-testid="testimonials-list">
          {!data ? <Loader2 className="animate-spin" /> : data.items.length === 0
            ? <p className="text-sm text-[#6E6E6E]" data-testid="testimonials-empty">Nothing {status} yet.</p>
            : data.items.map((t) => <Item key={t.id} t={t} api={api} onChange={load} />)}
        </div>
      </div>
    </div>
  );
}
